import {APIComponent, FormComponentProps} from "../DynamicFormTypes.ts";
import React, {useEffect, useRef} from "react";
import {useFormApi} from "../../../context/ApiContext.tsx";
import {useWatch} from "react-hook-form";
import {useFormActions, useFormInfo, useFormMethods, useFormStatic} from "../DyanmicFormContext.tsx";
import {loadingManager} from "../../../context/LoadingManager.ts";

const ApiComponentFieldRenderer: React.FC<FormComponentProps<APIComponent>> = ({
                                                                                  component
                                                                              }) => {
    const formAPI = useFormApi();
    const { formMethods } = useFormMethods()
    const {formPermissions} = useFormStatic()
    const { getActiveFormData } = useFormActions();
    const { applyAutoFillResponse } = useFormInfo();
    const renderCountRef = useRef(0);

    // Watch the specific field
    const watchedValue = useWatch({
        control: formMethods.control,
        name: component.onChangeFieldKey || "dummy_typescript_wants_this_to_not_be_undefined",
        disabled: !component.onChangeFieldKey
    });

    // Use useCallback to memoize the async function
    const handleChange = async () => {
        // Call the API with the value
        try {
            if (!formPermissions.allowTriggerApiComponent) {
                console.log("not applying api component changes because a is allowTriggerApiComponent is false", component.fieldKey, formPermissions);
                return;
            }
            if (!formPermissions.canEdit) {
                console.log("not applying api component changes because canedit is false", component.fieldKey, formPermissions);
                return;
            }
            //see if we already ran or not
            const value = formMethods.getValues(component.fieldKey);
            if (!component.onChangeFieldKey) {
                if (value && value === true) {
                    console.log("not applying api component changes because it already ran once and there is no onChangeFieldKey to watch", component.fieldKey);
                    return;
                }
            }
            else {
                if (value && value === formMethods.getValues(component.onChangeFieldKey)) {
                    console.log("not applying api component changes because the value is the same as the watched value and already ran", component.fieldKey, component.onChangeFieldKey);
                    return;
                }
            }
            loadingManager.show();

            // Make sure to call getValues as a function
            const response = await formAPI.callFromApiComponent({
                apiComponent: component,
                activeFormData: getActiveFormData()
            });

            let appliedFields = await applyAutoFillResponse(response, {
                triggerValidation: true,
                skipFieldKeysFor: [component.fieldKey] // skip validation for the field that triggered the API call, since it likely caused the error and we don't want to show an error for it until the user interacts with it again
            });

            //set value of self, to know when/what triggered and not trigger again
            if (appliedFields?.length > 0) {
                if (component.onChangeFieldKey) {
                    formMethods.setValue(component.fieldKey, watchedValue, {shouldValidate: false, shouldDirty: false, shouldTouch: false});
                }
                else {
                    formMethods.setValue(component.fieldKey, true, {shouldValidate: false, shouldDirty: false, shouldTouch: false});
                }
            }

            void formMethods.clearErrors()

        } catch (error) {
            console.error("API Error:", error);
        } finally {
            loadingManager.hide();
        }
    };

    useEffect(() => {
        renderCountRef.current += 1;
        if (!component.onChangeFieldKey && renderCountRef.current === 1) {
            console.debug(`ApiComponentField rendered ${renderCountRef.current} times`);

            //if there is no onChangeFieldKey, we only want to run this once, on the first render
            setTimeout(handleChange, 500);
        }

        // This effect will run whenever the watched field changes
        else if (component.onChangeFieldKey && watchedValue !== undefined) {
            console.debug("api component", component.fieldKey, "is watching", component.onChangeFieldKey, " for changes", "if undefined, it just runs once on mount");

            // Call the async function
            console.log("triggered change in api component", watchedValue, component.onChangeFieldKey);
            setTimeout(handleChange, 500);
        }
    }, [watchedValue, component.onChangeFieldKey]);

    return (
        <>
        </>
    );
}

export const ApiComponentField = React.memo(ApiComponentFieldRenderer);